  import React, { useState, useEffect } from "react";
  import {
    PlusCircle,
    Save,
    Trash2,
    Sun,
    Moon,
    GripVertical,
    AlertCircle,
    HelpCircle,
  } from "lucide-react";
  import CriteriaPreview from "../../components/CriteriaPreview";

  // Subtle tooltip that only shows on ? icon click
  const InfoTooltip = ({ text, className = "" }) => {
    const [isVisible, setIsVisible] = useState(false);

    return (
      <div className={`relative inline-block ${className}`}>
        <button
          onClick={() => setIsVisible(!isVisible)}
          className="p-1 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        >
          <HelpCircle className="w-4 h-4" />
        </button>
        {isVisible && (
          <>
            <div className="fixed inset-0" onClick={() => setIsVisible(false)} />
            <div className="absolute z-50 -top-2 left-1/2 -translate-x-1/2 -translate-y-full">
              <div className="bg-gray-900 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap shadow-lg">
                {text}
                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  // Mode Toggle component
  const ModeToggle = ({ isPercentageMode, onChange }) => (
    <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
      <button
        onClick={() => onChange(false)}
        className={`px-3 py-1 rounded text-sm font-medium transition-all ${
          !isPercentageMode
            ? "bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm"
            : "text-gray-600 dark:text-gray-300"
        }`}
      >
        Points
      </button>
      <button
        onClick={() => onChange(true)}
        className={`px-3 py-1 rounded text-sm font-medium transition-all ${
          isPercentageMode
            ? "bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm"
            : "text-gray-600 dark:text-gray-300"
        }`}
      >
        Percentage
      </button>
    </div>
  );

  // Simplified demo version of the slider for preview
  const DemoSlider = ({ label, maxPoints, isDarkMode }) => {
    const [value, setValue] = useState(0);

    const getGradientBackground = (value, maxPoints) => {
      const percentage = (value / maxPoints) * 100;
      return isDarkMode
        ? `linear-gradient(to right, 
          rgba(37, 99, 235, 1) 0%, 
          rgba(147, 51, 234, 1) ${percentage}%, 
          #374151 ${percentage}%, 
          #374151 100%)`
        : `linear-gradient(to right, 
          rgba(37, 99, 235, 1) 0%, 
          rgba(147, 51, 234, 1) ${percentage}%, 
          #e5e7eb ${percentage}%, 
          #e5e7eb 100%)`;
    };

    return (
      <div
        className={`flex items-center gap-4 p-2 rounded-lg transition-all
        ${isDarkMode ? "hover:bg-gray-700/30" : "hover:bg-gray-50"}`}
      >
        <div className="flex flex-col w-52">
          <span
            className={`font-medium ${
              isDarkMode ? "text-white" : "text-gray-700"
            }`}
          >
            {label}
          </span>
          <span
            className={`text-xs ${isDarkMode ? "text-white" : "text-gray-500"}`}
          >
            ({maxPoints}pts)
          </span>
        </div>

        <div className="flex-grow">
          <input
            type="range"
            min="0"
            max={maxPoints}
            step="0.5"
            value={value}
            onChange={(e) => setValue(parseFloat(e.target.value))}
            className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
            style={{
              background: getGradientBackground(value, maxPoints),
            }}
          />
        </div>

        <div className="w-20 text-right">
          <span
            className={`font-bold ${
              isDarkMode ? "text-blue-400" : "text-blue-600"
            }`}
          >
            {value.toFixed(1)}
          </span>
          <span
            className={`text-sm ml-1 ${
              isDarkMode ? "text-white" : "text-gray-500"
            }`}
          >
            /{maxPoints}
          </span>
        </div>
      </div>
    );
  };

  // Import the CriteriaPreview component at the top of the file
  // We'll need to add this import

  // Criterion Card
  const CriterionCard = ({
    criterion,
    onChange,
    onRemove,
    onDragStart,
    onDragEnd,
    onDragOver,
    isPercentageMode,
  }) => {
    const [error, setError] = useState("");

    const handlePointsChange = (value) => {
      const cleaned = value.replace(/[^\d.]/g, "");
      const parts = cleaned.split(".");
      const formatted = parts[0] + (parts.length > 1 ? "." + parts[1] : "");

      const numValue = parseFloat(formatted);
      const maxAllowed = 100;

      if (numValue > maxAllowed) {
        setError(
          `Maximum ${maxAllowed} ${isPercentageMode ? "%" : "points"} allowed`
        );
      } else if (numValue < 0) {
        setError(
          `${isPercentageMode ? "Percentage" : "Points"} cannot be negative`
        );
      } else {
        setError("");
      }

      onChange(criterion.id, "maxPoints", numValue || 0);
    };

    return (
      <div
        draggable
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragOver={onDragOver}
        className="group relative flex items-center gap-4 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all duration-200"
      >
        <div className="cursor-move p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
          <GripVertical className="h-5 w-5 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300" />
        </div>

        <div className="flex-1">
          <div className="flex items-center gap-4">
            <input
              type="text"
              value={criterion.label}
              onChange={(e) => onChange(criterion.id, "label", e.target.value)}
              placeholder="Criterion name (e.g., Technical Skill)"
              className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-900 rounded-md border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
            />
            <div className="flex items-center gap-2 min-w-[180px]">
              <input
                type="text"
                inputMode="decimal"
                value={criterion.maxPoints}
                onChange={(e) => handlePointsChange(e.target.value)}
                className={`w-20 px-3 py-2 bg-gray-50 dark:bg-gray-900 rounded-md border ${
                  error
                    ? "border-red-300 focus:ring-red-500"
                    : "border-gray-200 dark:border-gray-700 focus:ring-blue-500"
                } focus:ring-2 focus:border-transparent text-gray-900 dark:text-white text-right`}
              />
              <span className="text-sm text-gray-500 dark:text-white whitespace-nowrap">
                {isPercentageMode ? "% weight" : "points max"}
              </span>
            </div>
          </div>
          {error && (
            <div className="absolute left-14 -bottom-2 text-xs text-red-500 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {error}
            </div>
          )}
        </div>

        <button
          onClick={() => onRemove(criterion.id)}
          className="p-2 text-gray-400 hover:text-red-500 rounded-full hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </div>
    );
  };

  // Main component
  export default function CompetitionSettings() {
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [isPercentageMode, setIsPercentageMode] = useState(false);
    const [criteriaList, setCriteriaList] = useState([]);
    const [draggedItemIndex, setDraggedItemIndex] = useState(null);
    const [hasChanges, setHasChanges] = useState(false);

    const totalPoints = criteriaList.reduce(
      (sum, criterion) => sum + (criterion.maxPoints || 0),
      0
    );

    useEffect(() => {
      setHasChanges(criteriaList.length > 0);
    }, [criteriaList]);

    const handleAddCriterion = () => {
      setCriteriaList((prev) => [
        ...prev,
        { id: Date.now(), label: "", maxPoints: 0 },
      ]);
    };

    const handleCriterionChange = (id, field, value) => {
      setCriteriaList((prev) =>
        prev.map((c) => (c.id === id ? { ...c, [field]: value } : c))
      );
    };

    const handleRemoveCriterion = (id) => {
      setCriteriaList((prev) => prev.filter((c) => c.id !== id));
    };

    const handleDragStart = (index) => {
      setDraggedItemIndex(index);
    };

    const handleDragEnd = () => {
      setDraggedItemIndex(null);
    };

    const handleDragOver = (e, index) => {
      e.preventDefault();
      if (draggedItemIndex === null || draggedItemIndex === index) return;

      const newCriteria = [...criteriaList];
      const draggedItem = newCriteria[draggedItemIndex];
      newCriteria.splice(draggedItemIndex, 1);
      newCriteria.splice(index, 0, draggedItem);

      setCriteriaList(newCriteria);
      setDraggedItemIndex(index);
    };

    const handleSave = () => {
      if (totalPoints !== 100 && isPercentageMode) {
        alert("Total percentage must equal 100% before saving");
        return;
      }
      console.log("Saving criteria:", criteriaList);
      setTimeout(() => {
        setHasChanges(false);
        alert("Settings saved successfully!");
      }, 500);
    };

    return (
      <div className={isDarkMode ? "dark" : ""}>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-8">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Competition Settings
                </h1>
                <p className="text-gray-500 dark:text-white">
                  Configure your competition scoring system
                </p>
              </div>
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className="p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {isDarkMode ? (
                  <Sun className="w-5 h-5 text-yellow-500" />
                ) : (
                  <Moon className="w-5 h-5 text-gray-600" />
                )}
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Settings Panel */}
              <div className="space-y-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                  <div className="p-6 space-y-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                            Judging Criteria
                          </h2>
                          <InfoTooltip
                            text={
                              isPercentageMode
                                ? "Percentages must total 100%"
                                : "Each criterion can have up to 100 points"
                            }
                          />
                        </div>
                        <div className="mt-4">
                          <ModeToggle
                            isPercentageMode={isPercentageMode}
                            onChange={setIsPercentageMode}
                          />
                        </div>
                      </div>
                      <div
                        className={`text-sm ${
                          totalPoints > 100
                            ? "text-red-500"
                            : isPercentageMode && totalPoints === 100
                            ? "text-green-500"
                            : "text-gray-500 dark:text-white"
                        }`}
                      >
                        Total: {totalPoints}
                        {isPercentageMode ? "%" : " points"}
                      </div>
                    </div>

                    <div className="space-y-4">
                      {criteriaList.map((criterion, index) => (
                        <CriterionCard
                          key={criterion.id}
                          criterion={criterion}
                          onChange={handleCriterionChange}
                          onRemove={handleRemoveCriterion}
                          onDragStart={() => handleDragStart(index)}
                          onDragEnd={handleDragEnd}
                          onDragOver={(e) => handleDragOver(e, index)}
                          isPercentageMode={isPercentageMode}
                        />
                      ))}
                      <button
                        onClick={handleAddCriterion}
                        className="w-full p-4 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg text-gray-500 dark:text-white hover:border-blue-500 hover:text-blue-500 dark:hover:text-blue-400 transition-colors group"
                      >
                        <div className="flex items-center justify-center gap-2">
                          <PlusCircle className="w-5 h-5 transition-transform group-hover:scale-110" />
                          <span>Add Judging Criterion</span>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Preview Panel */}
              <div className="space-y-4">
                <div
                  className={`rounded-xl p-6 ${
                    isDarkMode ? "bg-gray-800" : "bg-white"
                  } shadow-lg border border-gray-200 dark:border-gray-700`}
                >
                  <CriteriaPreview
                    criteria={criteriaList}
                    isDarkMode={isDarkMode}
                    showTitle={true}
                  />
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <button
                onClick={handleSave}
                disabled={
                  !hasChanges ||
                  totalPoints > 100 ||
                  (isPercentageMode && totalPoints !== 100)
                }
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                  hasChanges &&
                  totalPoints <= 100 &&
                  (!isPercentageMode || totalPoints === 100)
                    ? "bg-blue-600 hover:bg-blue-700 text-white"
                    : "bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-800 dark:text-gray-500"
                }`}
              >
                <Save className="w-5 h-5" />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
